import {
  AppShell,
  Button,
  NoAccess,
  Page,
  Sidebar,
  ToastProvider,
  type RenderLink,
} from '@laura/design-system';
import {
  Loading,
  Placeholder,
  SecurityCard,
  SignInScreen,
  useSession,
  ConfirmProvider,
  useRouteFocus,
  preloadAfterPaint,
  preloadProps,
} from '@laura/portal-kit';
import { isStaffRole, type StaffRole } from '@laura/shared/permissions';
import { useQuery } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { canOpen, navItems, STAFF_MODULES, STAFF_ROLE_LABEL } from './nav.js';
import { PRELOAD_AFTER_PAINT, preloadFor } from './routes.js';

/** `GET /me` on control-plane-api. */
export interface StaffMe {
  sub: string;
  email: string | null;
  role: StaffRole;
  permissions: string[];
}

// Hovering or focusing a link preloads its module's chunk (plan 45 phase 2).
const routerLink: RenderLink = ({ href, children, ...rest }) => (
  <Link to={href} {...preloadProps(href, preloadFor)} {...rest}>
    {children}
  </Link>
);

/** Module routes; `pages` lets later phases swap a placeholder for the real screen. */
export function App({ pages = {} }: { pages?: Record<string, ReactNode> }) {
  const session = useSession();
  const location = useLocation();
  useRouteFocus('Novations');
  const signedIn = session.status === 'signedIn';
  useEffect(() => {
    if (signedIn) preloadAfterPaint(PRELOAD_AFTER_PAINT);
  }, [signedIn]);
  const me = useQuery({
    queryKey: ['me', session.claims?.sub],
    queryFn: () => session.api.get<StaffMe>('/me'),
    enabled: session.status === 'signedIn',
    retry: false,
  });

  if (session.status === 'loading') return <Loading />;
  if (session.status === 'signedOut')
    return (
      <div data-theme="novations">
        <SignInScreen
          product="Novations back office"
          backend={session.backend}
          onSignedIn={(email) => {
            session.rememberEmail(email);
            // ADR 160: TOTP stays first; an emailed code becomes the fallback. Where the
            // pool has no email MFA yet (no SES identity), Cognito refuses and nothing changes.
            void session.backend.allowEmailFallback?.().catch(() => undefined);
            void session.refresh();
          }}
        />
      </div>
    );

  const role = session.claims?.role;
  if (!isStaffRole(role))
    return (
      <NoAccess
        page="the Novations back office"
        role="a user without a staff role"
        needed="a staff role"
      />
    );

  const current =
    location.pathname === '/account'
      ? 'account'
      : STAFF_MODULES.find((m) => location.pathname.startsWith(m.path))?.id;
  return (
    <ToastProvider>
      <ConfirmProvider>
        <AppShell
          theme="novations"
          sidebar={(onNavigate) => (
            <Sidebar
              org="Novations"
              who={`${me.data?.email ?? 'Signed in'} · ${STAFF_ROLE_LABEL[role]}`}
              sections={[
                { label: 'Platform', items: navItems(role) },
                { label: 'You', items: [{ id: 'account', label: 'My account', href: '/account' }] },
              ]}
              renderLink={routerLink}
              footer="Laura control plane"
              {...(current && { currentId: current })}
              {...(onNavigate && { onNavigate })}
            />
          )}
          topbar={
            <Button small onClick={() => void session.signOut()}>
              Sign out
            </Button>
          }
        >
          <Routes>
            <Route path="/" element={<Navigate to="/clinics" replace />} />
            <Route
              path="/account"
              element={
                <Page title="My account" subtitle={me.data?.email ?? undefined}>
                  <SecurityCard mfaNote="Two-step sign-in is always on for Novations staff." />
                </Page>
              }
            />
            {STAFF_MODULES.map((m) => (
              <Route
                key={m.id}
                path={`${m.path}/*`}
                element={
                  !canOpen(m, role) ? (
                    <NoAccess
                      page={m.label}
                      role={STAFF_ROLE_LABEL[role]}
                      needed={m.roles.map((r) => STAFF_ROLE_LABEL[r]).join(' or ')}
                    />
                  ) : (
                    (pages[m.id] ?? <Placeholder title={m.label} what={m.what} epic={m.epic} />)
                  )
                }
              />
            ))}
            <Route
              path="*"
              element={
                <Placeholder
                  title="Page not found"
                  what="This address does not exist."
                  epic="no epic"
                />
              }
            />
          </Routes>
        </AppShell>
      </ConfirmProvider>
    </ToastProvider>
  );
}
